import * as R from "ramda";
import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { bindInstance } from "redux-mvc";
import "flatpickr/dist/themes/airbnb.css";
import moment from "moment";
import MomentUtils from "@date-io/moment";
import {
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider
} from "@material-ui/pickers";
import classNames from "classnames";

import { noop } from "utils/General";

import Flatpickr from "react-flatpickr";

import { WithFormInstanceConsumer } from "../Context";
import { actions } from "../../model";
import { getValue } from "../../selectors";
import {
  FIELD_TYPES,
  DEFAULT_DATE_FORMAT,
  DATE_FORMAT_MATERIAL
} from "../../constants";

import CSSModules from "react-css-modules";
import css from "./styles.scss";

const decorate = R.compose(
  WithFormInstanceConsumer({
    fieldType: FIELD_TYPES.DATETIME
  }),
  connect(
    (state, props) => ({
      value: getValue(state, props)
    }),
    bindInstance({
      onChange: actions.setFieldValue,
      setIniValue: actions.setIniValue,
      onBlur: actions.blurField
    })
  ),
  CSSModules(css)
);

const formatDate = (date, format) =>
  moment([
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds()
  ]).format(format);

export const DateTimeInput = decorate(
  ({
    format = DEFAULT_DATE_FORMAT,
    dateFormat = "",
    disabled,
    fieldId,
    fieldType,
    onChange = noop,
    onBlur = noop,
    value = null,
    iniValue,
    setIniValue = noop,
    inputClassName = css.dateTimeInput,
    altFormat = "MMM D, YYYY @ h:mm a",
    enableTime = true,
    noCalendar = false,
    minDate = null,
    maxDate = null,
    "data-cy": dataCy,
    materialUI = false,
    modalVariant = false,
    errorMessage,
    customFormat = DATE_FORMAT_MATERIAL,
    removeDateFormatHelper = false
  }) => {
    useEffect(() => {
      setIniValue(iniValue, {
        meta: { fieldId, fieldType, format: dateFormat }
      });
    }, []);

    const muiClasses = classNames(css.keyboardDateTimeInput, {
      [css.modalvariant]: modalVariant
    });

    const isErrorOnTime =
      (minDate &&
        moment(value)
          .seconds(0)
          .milliseconds(0)
          .isBefore(minDate)) ||
      (maxDate &&
        moment(value)
          .seconds(0)
          .milliseconds(0)
          .isAfter(maxDate));

    const inputRef = useRef(null);

    return materialUI ? (
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <KeyboardDateTimePicker
          onOpen={() => {
            setTimeout(() => {
              const actionsRoot = document.getElementsByClassName(
                "MuiDialogActions-root"
              )[0];
              const buttons = actionsRoot.querySelectorAll("button");
              actionsRoot.insertBefore(buttons[1], buttons[0]);
            }, 100);
          }}
          className={muiClasses}
          DialogProps={{ className: css.dialog, disableEnforceFocus: true }}
          value={value}
          minDate={minDate ? minDate : undefined}
          maxDate={maxDate ? maxDate : undefined}
          allowKeyboardControl
          inputVariant="outlined"
          format={customFormat || DATE_FORMAT_MATERIAL}
          helperText={
            isErrorOnTime
              ? errorMessage ||
                "The time range should be before the minimum time"
              : !removeDateFormatHelper
              ? DATE_FORMAT_MATERIAL
              : null
          }
          onChange={(momVal, plainVal) => {
            if (!plainVal?.includes("_")) {
              const input = document.querySelector(".MuiInputBase-root");
              if (input) {
                input.classList.add("Mui-focused");
                inputRef.current = input.querySelector("input");
              }
              onChange(momVal?.toDate() ? momVal?.toDate() : null, {
                meta: {
                  fieldId,
                  fieldType,
                  format,
                  ...(input && { containerRef: inputRef })
                }
              });
            }
          }}
          data-cy={dataCy}
          error={isErrorOnTime}
          disabled={disabled}
          onBlur={() => onBlur(null, { meta: { fieldId, fieldType } })}
        />
      </MuiPickersUtilsProvider>
    ) : (
      <Flatpickr
        className={inputClassName}
        disabled={disabled}
        options={{
          minDate,
          maxDate,
          altInput: true,
          altFormat: altFormat,
          enableTime,
          noCalendar,
          minuteIncrement: 1,
          dateFormat,
          formatDate
        }}
        value={value}
        onChange={val => {
          onChange(val[0], {
            meta: { fieldId, fieldType, format }
          });
        }}
        onBlur={() => onBlur(null, { meta: { fieldId, fieldType } })}
        data-cy={dataCy}
      />
    );
  }
);
